import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Button, Checkbox, SearchableSelect } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * NewInformationRequestModal — ask a business for something. Two steps:
 *   1. Compose — pick the business and the documents to request (from the org's
 *      master document-types list), plus an optional title and due date.
 *   2. Preview — the exact email and recipient list a send would produce
 *      (POST /api/information-requests/preview), then either create the draft or
 *      create and send it.
 *
 * The request is created as a DRAFT (POST /api/information-requests/bulk), which is
 * silent; "Send now" then sends it via POST /:id/send, which emails the people the
 * user kept selected. The recipients approved in the preview are exactly who is
 * emailed — both steps resolve them through the same server-side resolver, and the
 * send re-validates each address against that business's own people.
 *
 * `defaultClientId` pre-selects and LOCKS the business (used from a business
 * profile — the prop keeps the DB's table name, `clients`). Pure props in; the
 * caller invalidates its own list via `onCreated`.
 */

interface NewInformationRequestModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select + lock the business (from a business profile). */
  defaultClientId?: string;
  /** Called after a successful create/send (before onClose) so the caller can invalidate. */
  onCreated?: () => void;
}

interface NamedRow { id: string; name: string | null; }
interface PreviewRecipient { name: string; email: string; role: string | null; }
interface PreviewResponse {
  subject: string;
  body_text: string;
  recipients: PreviewRecipient[];
  recipients_total: number;
}
interface BulkResponse { created: Array<{ id: string; client_id: string; title: string }>; count: number; }

export function NewInformationRequestModal({ open, onClose, defaultClientId, onCreated }: NewInformationRequestModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [step, setStep] = useState<'compose' | 'preview'>('compose');
  const [clientId, setClientId] = useState<string | null>(defaultClientId ?? null);
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  // Which candidate emails will actually be sent to. Seeded to ALL on preview, then
  // the user can deselect down to a subset.
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

  // The modal is a PERSISTENT mount (open just toggles), so the useState initializers
  // above only ran once — at first mount, when the caller's props were still null.
  // Re-seed the form from the CURRENT props every time it opens, so a per-card launch
  // actually pre-selects its business and no stale selection leaks between opens.
  useEffect(() => {
    if (!open) return;
    setStep('compose');
    setClientId(defaultClientId ?? null);
    setTypes(new Set());
    setTitle(''); setDueDate(''); setPreview(null); setSelectedEmails(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const businessLocked = !!defaultClientId;

  // Data — businesses (unless locked) and the master document-type list.
  const businessesQuery = useQuery<NamedRow[]>({
    queryKey: ['clients-for-info-request'],
    queryFn: ({ signal }) => api.get<NamedRow[]>('/api/clients', signal),
    enabled: open && !businessLocked,
  });
  // Locked business (launched from a business profile): fetch just its name so the
  // field + preview summary show it by NAME, not the raw id.
  const lockedBusinessQuery = useQuery<NamedRow>({
    queryKey: ['client-for-info-request', defaultClientId],
    queryFn: ({ signal }) => api.get<NamedRow>(`/api/clients/${defaultClientId}`, signal),
    enabled: open && businessLocked,
  });
  const docTypesQuery = useQuery<string[] | { values?: string[] }>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
    enabled: open,
  });

  const docTypes = normalizeValues(docTypesQuery.data);
  const businessName = businessLocked
    ? (lockedBusinessQuery.data?.name ?? undefined)
    : (businessesQuery.data ?? []).find(c => c.id === clientId)?.name ?? undefined;

  function reset() {
    setStep('compose');
    setClientId(defaultClientId ?? null);
    setTypes(new Set());
    setTitle(''); setDueDate('');
    setPreview(null);
  }
  function close() { reset(); onClose(); }

  const chosenTypes = [...types];
  const composeValid = !!clientId && chosenTypes.length > 0;

  function requestBody() {
    return {
      client_id: clientId,
      document_types: chosenTypes,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(dueDate ? { due_date: dueDate } : {}),
    };
  }

  const previewMut = useMutation<PreviewResponse, Error, void>({
    mutationFn: () => api.post<PreviewResponse>('/api/information-requests/preview', {
      client_id: clientId, title: title.trim() || undefined, document_types: chosenTypes,
    }),
    onSuccess: (data) => { setPreview(data); setSelectedEmails(new Set(data.recipients.map((r) => r.email))); setStep('preview'); },
    onError: (err) => toast({ variant: 'error', message: apiError(err, 'Could not build the preview.') }),
  });

  // Create the draft(s); optionally send right away. Send is BEST-EFFORT: a send
  // failure must NOT reject the whole mutation (the draft already exists), else the
  // modal would stay open and a retry would re-run /bulk — duplicating requests and
  // re-emailing. Failed sends are reported; those drafts remain sendable individually
  // from the board.
  const createMut = useMutation<{ count: number; sent: number; sendFailed: number; requested: boolean }, Error, boolean>({
    mutationFn: async (send: boolean) => {
      const res = await api.post<BulkResponse>('/api/information-requests/bulk', requestBody());
      let sent = 0, sendFailed = 0;
      if (send) {
        // Email only the recipients the user kept selected (validated server-side
        // against the request's own candidates).
        const recipient_emails = [...selectedEmails];
        for (const r of res.created) {
          try { await api.post(`/api/information-requests/${r.id}/send`, { recipient_emails }); sent++; }
          catch { sendFailed++; }
        }
      }
      return { count: res.count, sent, sendFailed, requested: send };
    },
    onSuccess: ({ count, sent, sendFailed, requested }) => {
      queryClient.invalidateQueries({ queryKey: ['information-requests-board'] });
      queryClient.invalidateQueries({ queryKey: ['information-requests'] });
      const noun = (n: number) => (n === 1 ? 'request' : 'requests');
      if (!requested) {
        toast({ variant: 'success', message: `${count} draft ${noun(count)} created.` });
      } else if (sendFailed === 0) {
        toast({ variant: 'success', message: `${sent} ${noun(sent)} sent.` });
      } else {
        toast({ variant: 'info', message: `${count} created; ${sent} sent, ${sendFailed} couldn't be sent — send them from the board.` });
      }
      onCreated?.();
      close();
    },
    onError: (err) => toast({ variant: 'error', message: apiError(err, 'Could not create the request.') }),
  });

  const busy = previewMut.isPending || createMut.isPending;

  function toggle(set: Set<string>, apply: (s: Set<string>) => void, id: string) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    apply(next);
  }

  // Recipient selection derived state.
  const totalRecipients = preview?.recipients.length ?? 0;
  const allRecipientsSelected = totalRecipients > 0 && selectedEmails.size === totalRecipients;
  const toggleAllRecipients = () =>
    setSelectedEmails(allRecipientsSelected ? new Set() : new Set((preview?.recipients ?? []).map((r) => r.email)));

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) close(); }}
      title={step === 'compose' ? 'New information request' : 'Preview & send'}
      size="lg"
      footer={
        step === 'compose' ? (
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              loading={previewMut.isPending}
              disabled={!composeValid || busy}
              onClick={() => previewMut.mutate()}
            >Next: preview</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setStep('compose')} disabled={busy}>Back</Button>
            <Button variant="secondary" onClick={() => createMut.mutate(false)} disabled={busy}>Create draft</Button>
            <Button
              variant="primary"
              loading={createMut.isPending}
              disabled={busy || selectedEmails.size === 0}
              onClick={() => createMut.mutate(true)}
            >Send now{selectedEmails.size > 0 ? ` (${selectedEmails.size})` : ''}</Button>
          </>
        )
      }
    >
      {step === 'compose' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {/* Business */}
          <label style={{ display: 'block' }}>
            <span style={labelStyle}>Business</span>
            {businessLocked ? (
              <div style={lockedStyle}>{businessName ?? defaultClientId} <span style={{ color: 'var(--color-ink-3)' }}>(from context)</span></div>
            ) : (
              <SearchableSelect
                options={(businessesQuery.data ?? []).map(c => ({ value: c.id, label: c.name || c.id }))}
                value={clientId}
                onChange={(v) => setClientId(v)}
                placeholder={businessesQuery.isLoading ? 'Loading businesses…' : 'Search businesses…'}
                aria-label="Business"
              />
            )}
          </label>

          {/* Documents to request */}
          <div>
            <span style={labelStyle}>Documents to request</span>
            {docTypesQuery.isLoading ? (
              <p style={hintStyle}>Loading document types…</p>
            ) : docTypes.length === 0 ? (
              <p style={hintStyle}>No document types configured.</p>
            ) : (
              <div style={checkListStyle}>
                {docTypes.map(t => (
                  <Checkbox key={t} label={t} checked={types.has(t)} onChange={() => toggle(types, setTypes, t)} />
                ))}
              </div>
            )}
          </div>

          {/* Optional details */}
          <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 200px' }}>
              <span style={labelStyle}>Title <span style={optStyle}>(optional)</span></span>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document request" />
            </label>
            <label style={{ flex: '0 0 160px' }}>
              <span style={labelStyle}>Due date <span style={optStyle}>(opt.)</span></span>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </label>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div style={summaryStyle}>
            Creating <strong>1</strong> request
            {businessName ? <> for <strong>{businessName}</strong></> : null}, asking
            for <strong>{chosenTypes.length}</strong> document{chosenTypes.length === 1 ? '' : 's'}.
          </div>

          <div>
            <span style={labelStyle}>Email subject</span>
            <div style={fieldBoxStyle}>{preview?.subject}</div>
          </div>
          <div>
            <span style={labelStyle}>Email body</span>
            <pre style={{ ...fieldBoxStyle, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{preview?.body_text}</pre>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
              <span style={labelStyle}>Recipients ({selectedEmails.size}/{totalRecipients} selected)</span>
              {totalRecipients > 0 && (
                <button
                  type="button"
                  onClick={toggleAllRecipients}
                  style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}
                >{allRecipientsSelected ? 'Clear all' : 'Select all'}</button>
              )}
            </div>
            {totalRecipients === 0 ? (
              <p style={{ ...hintStyle, color: 'var(--color-status-red)' }}>
                Nobody at this business has an email on file — add one before sending. You can still create a draft.
              </p>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', maxHeight: 220, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)' }}>
                  {preview?.recipients.map(r => (
                    <label key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
                      <input type="checkbox" checked={selectedEmails.has(r.email)} onChange={() => toggle(selectedEmails, setSelectedEmails, r.email)} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}{r.role ? ` · ${r.role}` : ''} &lt;{r.email}&gt;
                      </span>
                    </label>
                  ))}
                </div>
                {selectedEmails.size === 0 && (
                  <p style={{ ...hintStyle, color: 'var(--color-status-red)', margin: 'var(--sp-1) 0 0' }}>
                    None selected — pick at least one to send, or use Create draft.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function normalizeValues(data: string[] | { values?: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}
function apiError(err: unknown, fallback: string): string {
  return err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
    ? String((err.body as { error: unknown }).error)
    : fallback;
}

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)',
};
const optStyle: React.CSSProperties = { fontWeight: 'var(--fw-regular)', color: 'var(--color-ink-3)' };
const hintStyle: React.CSSProperties = { margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' };
const lockedStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', fontSize: 'var(--fs-md)', color: 'var(--color-ink)',
};
const checkListStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', marginTop: 'var(--sp-2)',
  maxHeight: 200, overflowY: 'auto', padding: 'var(--sp-2)',
  border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', background: 'var(--color-bg)',
};
const summaryStyle: React.CSSProperties = {
  padding: 'var(--sp-3)', background: 'var(--brand-primary-bg)', borderRadius: 'var(--r-md)',
  fontSize: 'var(--fs-md)', color: 'var(--color-ink)',
};
const fieldBoxStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
};
